import ReactJsonSchemaForm from '@bht/react-jsonschema-form/rjsf/core';
import ValidatorAJV8 from '@bht/react-jsonschema-form/rjsf/validator';
import { RjsfCustomEvents } from '@bht/react-jsonschema-form/internal/constants';

import {
  get,
  set,
  isEmpty,
} from 'lodash-es';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { messages } from '../../../helpers/intl.helper';
import logger from '../../../../core/logger';

import { STATE__AUTHORIZED } from '../../../constants';

import SaveButtonImage from '../../../assets/icons/save-white.svg';
import SendButtonImage from '../../../assets/icons/send-white.svg';
import ExportButtonImage from '../../../assets/icons/export-white.svg';

import ActionButton from '../../../shared/components/actionButton';
import Alert from '../../../shared/components/alert';
import FormNotification from '../../../shared/components/formNotification';
import Loader from '../../../shared/components/loader';
import RecaptchaVerification from '../../../shared/components/recaptchaVerification';
import { scrollToNearestVerticalPageEdge } from '../../../shared/helpers/scroll.helper';
import { QuestionnaireFormValidationErrorsInfo } from './helper';

import transformErrors from './helpers/formValidationHelper';

const Questionnaire = forwardRef(({
  title,
  schema,
  uiSchema,
  fields,
  widgets,
  isFetching,
  isSavingDraft,
  onSaveDraft,
  setQuestionnaireData,
  questionnaireData,
  isSending,
  isExporting,
  onExportQuestionnaire,
  message,
  onSendQuestionnaire,
  onDeleteQuestionnaire,
  rawSchemaRequired,
}, ref) => {
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);
  const formRefHidden = useRef(null);

  const state = useSelector((store) => store.application.get('state'));
  const isReadOnly = useSelector((store) => store.authorizedState.get('readOnlyElement'));

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(state !== STATE__AUTHORIZED);
  const [liveValidate, setLiveValidate] = useState(false);
  const [isSendButtonClick, setIsSendButtonClick] = useState(false);

  // Flaga określająca, czy komunikat o wystąpieniu błędów walidacji ma zostać wyświetlony użytkownikowi.
  const [isValidationErrorsInfoVisible, setIsValidationErrorsInfoVisible] = useState(false);

  // Zdarzenie zmieny formularza/ankiety
  const onQuestionnaireDataChange = (event) => {
    setQuestionnaireData(event.formData);
  };

  const onRecaptchaVerify = (isVerificationSuccess) => {
    if (isVerificationSuccess) {
      setSubmitButtonDisabled(false);
    } else {
      setSubmitButtonDisabled(true);
    }
  };

  // Metoda odpowiedzialna za automatyczną modyfikację wartości pól.
  const setDataByVisibility = (sectionId, fieldId, visible, defaultValue, additionalData) => {
    let changeRequired = false;
    // Przywrócono pierwotny stan - usunięto `cloneDeep(questionnaireData)`, aby uniknąć przypadków nieaktualnych danych.
    const modifiedEditorState = questionnaireData;

    if (sectionId) {
      const parents = sectionId.split('_');
      if (parents.length > 1) {
        for (let i = parents.length - 1; i > 0; i -= 1) {
          parents[i] = parents.slice(0, i + 1).join('_');
        }
      }

      const id = `${parents.join('.')}.${fieldId}`;
      const value = get(modifiedEditorState, id);

      if (visible && !value && defaultValue) {
        // Przypadek w którym pole zdefiniowane w sekcji zostało wyświetlone i należy przypisać mu wartość domyślną.
        set(
          modifiedEditorState,
          id,
          defaultValue,
        );

        logger.debug(`Do pola ${id} przypisano wartość domyślną (${defaultValue}).`);

        changeRequired = true;
      }

      if (!visible && (value || value === 0)) {
        // Przypadek w którym pole zdefiniowane w sekcji zostało ukryte i należy przypisać mu wartość pustą.
        // W przypadku ukrycia sekcji lub pola podrzędnego powinniśmy wyczyścić wszystkie pola zawarte w środku.
        const { allChildFieldsDataPaths } = additionalData || {};

        allChildFieldsDataPaths?.forEach?.((path) => {
          set(modifiedEditorState, path, undefined);
          logger.debug(`Do pola ${path} przypisano wartość pustą.`);
        });

        logger.debug(`Do pola ${id} przypisano wartość pustą.`);

        changeRequired = true;
      }
    } else if (visible && !modifiedEditorState[fieldId] && defaultValue) {
      // Przypadek w którym pole zostało wyświetlone i należy przypisać mu wartość domyślną.
      modifiedEditorState[fieldId] = defaultValue;

      changeRequired = true;
    } else if (!visible && modifiedEditorState[fieldId]) {
      // Przypadek w którym pole zostało ukryte i należy przypisać mu wartość pustą.
      modifiedEditorState[fieldId] = undefined;

      changeRequired = true;
    }

    if (changeRequired) {
      setQuestionnaireData({
        ...modifiedEditorState,
        forceUpdate: Date.now(),
      });
    }
  };

  const setFormFieldData = (fieldData, newValue) => {
    const { fieldFullPath, action } = fieldData || {};

    if (fieldFullPath) {
      const modifiedEditorState = questionnaireData;

      if ([
        RjsfCustomEvents.FieldVisibilityUnsetValue
      ].includes(action)) {
        set(modifiedEditorState, fieldFullPath, newValue);
        setQuestionnaireData({
          ...modifiedEditorState,
          forceUpdate: Date.now(),
        });

        logger.debug(`[${action}] Wartość pola ${fieldFullPath} została zmieniona: ${newValue}.`);
      }
    }
  }

  const onAutomaticFieldValueCalculated = (fieldId, value) => {
    // Przywrócono pierwotny stan, czyli usunięto `cloneDeep(editorState)`,
    // aby przy automatycznym wyl. pól (czyli autonomicznych, bardzo szybkich zmianach WIELU wartości),
    // ZAWSZE operować na aktualnych wartościach.
    const modifiedEditorState = questionnaireData;

    set(
      modifiedEditorState,
      fieldId,
      value,
    );

    setQuestionnaireData({
      ...modifiedEditorState,
      forceUpdate: Date.now(),
    });
  };

  const onSendButtonClick = () => {
    setLiveValidate(true);
    setIsSendButtonClick(true);
  };

  const onExportButtonClick = () => {
    onExportQuestionnaire();
  };

  const onDeleteButtonClick = () => {
    onDeleteQuestionnaire();
  };

  const additionalSubfieldValidation = (formData, errors) => {
    const checkIfSubfieldIsEmpty = (fieldName) => {
      let isEmptyField = false;

      if (Array.isArray(formData[fieldName])) {
        formData[fieldName].forEach((item) => {
          if (isEmpty(item)) {
            isEmptyField = true;
          }
        });
      }

      if (typeof formData[fieldName] === 'object') {
        Object.keys(formData[fieldName]).forEach((key) => {
          if (!formData[fieldName][key]) {
            isEmptyField = true;
          }
        });
      }

      return isEmptyField;
    };

    rawSchemaRequired.forEach((requiredField) => {
      if (checkIfSubfieldIsEmpty(requiredField)) {
        errors[requiredField].addError('Pole wymagane.');
      }
    });
    return errors;
  };

  useEffect(() => {
    if (isSendButtonClick) {
      setIsSendButtonClick(false);
      // Sprawdzenie czy nie ma błędów walidacji
      const { errors } = formRef?.current?.validate?.(questionnaireData) || {};

      if (errors?.length === 0) {
        if (formRef?.current?.props) {
          formRef.current.props.onSubmit();
        }
        setIsValidationErrorsInfoVisible(false);
      } else {
        setIsValidationErrorsInfoVisible(true);
        scrollToNearestVerticalPageEdge();
      }
    }
  }, [isSendButtonClick]);

  useImperativeHandle(ref, () => ({
    getHtml() {
      return formRefHidden?.current.formElement;
    },
  }));

  return (
    <div className="questionnaire">
      <Choose>
        <When condition={isFetching}>
          <Loader />
        </When>
        <When condition={schema}>
          <div className="questionnaire-header">
            {title}
          </div>
          <div className="questionnaire-content">
            <FormNotification message={message} />
            <If condition={isValidationErrorsInfoVisible}>
              <Alert text={QuestionnaireFormValidationErrorsInfo} type="danger" />
            </If>
            <Choose>
              <When condition={!isReadOnly}>
                <div className="buttons">
                  <If condition={state === STATE__AUTHORIZED}>
                    <ActionButton
                      title="Zapisz bez wysyłania"
                      isLoading={isSavingDraft}
                      logoImgSrc={SaveButtonImage}
                      onClick={onSaveDraft}
                      imgLogoAlt="Zapisz bez wysyłania"
                      className="separator-right"
                      type="button"
                    />
                  </If>
                  <ActionButton
                    title="Wyślij"
                    isLoading={isSending}
                    disabled={submitButtonDisabled}
                    logoImgSrc={SendButtonImage}
                    onClick={() => onSendButtonClick()}
                    imgLogoAlt="Wyślij"
                    className="separator-right"
                    type="button"
                  />
                  <ActionButton
                    title="Anuluj"
                    onClick={() => onDeleteButtonClick()}
                    imgLogoAlt="Anuluj"
                    type="button"
                  />
                </div>
              </When>
              <Otherwise>
                <div className="buttons">
                  <ActionButton
                    title="Eksport"
                    isLoading={isExporting}
                    logoImgSrc={ExportButtonImage}
                    onClick={() => onExportButtonClick()}
                    imgLogoAlt="Eksport"
                    className="separator-right"
                    type="button"
                  />
                  <ActionButton
                    title="Anuluj"
                    onClick={() => onDeleteButtonClick()}
                    imgLogoAlt="Anuluj"
                    type="button"
                  />
                </div>
              </Otherwise>
            </Choose>
            <ReactJsonSchemaForm
              ref={formRef}
              disabled={isReadOnly}
              onSubmit={onSendQuestionnaire}
              transformErrors={(errors) => transformErrors(errors, schema, uiSchema, formRef?.current?.state?.formData)}
              formContext={{
                ...questionnaireData,
                onAutomaticFieldValueCalculated,
                setDataByVisibility,
                setFormFieldData,
                intl: { messages },
              }}
              formData={questionnaireData}
              // formData={{}}
              schema={schema}
              uiSchema={uiSchema}
              fields={fields}
              widgets={widgets}
              noHtml5Validate
              showErrorList={false}
              validator={ValidatorAJV8}
              liveValidate={liveValidate}
              validate={additionalSubfieldValidation}
              onChange={onQuestionnaireDataChange}
            >
              <FormNotification message={message} />
              <If condition={isValidationErrorsInfoVisible}>
                <Alert text={QuestionnaireFormValidationErrorsInfo} type="danger" />
              </If>
              <Choose>
                <When condition={isReadOnly}>
                  <div className="actions">
                    <ActionButton
                      title="Eksport"
                      isLoading={isExporting}
                      logoImgSrc={ExportButtonImage}
                      onClick={() => onExportQuestionnaire()}
                      imgLogoAlt="Eksport"
                      className="separator-right"
                      type="button"
                    />
                    <ActionButton
                      title="Anuluj"
                      onClick={() => onDeleteButtonClick()}
                      imgLogoAlt="Anuluj"
                      type="button"
                    />
                  </div>
                </When>
                <Otherwise>
                  <If condition={state !== STATE__AUTHORIZED}>
                    <RecaptchaVerification
                      className="form-group"
                      onRecaptchaVerify={onRecaptchaVerify}
                      recaptchaRef={recaptchaRef}
                    />
                  </If>
                  <div className="actions">
                    <If condition={state === STATE__AUTHORIZED}>
                      <ActionButton
                        title="Zapisz bez wysyłania"
                        isLoading={isSavingDraft}
                        logoImgSrc={SaveButtonImage}
                        onClick={onSaveDraft}
                        imgLogoAlt="Zapisz bez wysyłania"
                        className="separator-right"
                        type="button"
                      />
                    </If>
                    <ActionButton
                      title="Wyślij"
                      isLoading={isSending}
                      disabled={submitButtonDisabled}
                      logoImgSrc={SendButtonImage}
                      onClick={() => onSendButtonClick()}
                      imgLogoAlt="Wyślij"
                      className="separator-right"
                      type="submit"
                    />
                    <ActionButton
                      title="Anuluj"
                      onClick={() => onDeleteButtonClick()}
                      imgLogoAlt="Anuluj"
                      type="button"
                    />
                  </div>
                </Otherwise>
              </Choose>
            </ReactJsonSchemaForm>
            <div hidden>
              <ReactJsonSchemaForm
                ref={formRefHidden}
                disabled={isReadOnly}
                formContext={{
                  ...questionnaireData,
                  onAutomaticFieldValueCalculated,
                  setDataByVisibility,
                  setFormFieldData,
                  intl: { messages },
                }}
                formData={questionnaireData}
                schema={schema}
                uiSchema={uiSchema}
                fields={fields}
                widgets={widgets}
                noHtml5Validate
                validator={ValidatorAJV8}
                showErrorList={false}
              />
            </div>
          </div>
        </When>
        <Otherwise>
          <div className="no-data-to-display">
            Brak danych do wyświetlenia
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
});

Questionnaire.propTypes = {
  // Tytuł.
  title: PropTypes.string,
  // Schemat.
  schema: PropTypes.shape(),
  // Schmat (UI).
  uiSchema: PropTypes.shape(),
  // Pola.
  fields: PropTypes.shape(),
  // Widgety.
  widgets: PropTypes.shape(),
  // Pobieranie formularza
  isFetching: PropTypes.bool,
  // Zapisywanie wersji roboczej
  isSavingDraft: PropTypes.bool,
  // Akcja zapisu formularza roboczego
  onSaveDraft: PropTypes.func.isRequired,
  // Struktura Formularza
  questionnaireData: PropTypes.object, // eslint-disable-line
  // akcja zmiany Formularza
  setQuestionnaireData: PropTypes.func.isRequired,
  // Akcja wysłania formularza
  onSendQuestionnaire: PropTypes.func.isRequired,
  // Notyfikacje
  message: PropTypes.shape({
    value: PropTypes.string,
    type: PropTypes.string,
    length: PropTypes.number,
  }),
  // Zapis Formularza
  isSending: PropTypes.bool,
  // Export Formularza
  isExporting: PropTypes.bool,
  // Akcja exportu formularza
  onExportQuestionnaire: PropTypes.func.isRequired,
  // Akcja anulowania wypełniania formularza
  onDeleteQuestionnaire: PropTypes.func.isRequired,
  // Wymagane pola
  rawSchemaRequired: PropTypes.arrayOf(PropTypes.string),
};

Questionnaire.defaultProps = {
  schema: {},
  uiSchema: {},
  fields: {},
  widgets: {},
  isFetching: false,
  title: '',
  questionnaireData: {},
  isSavingDraft: false,
  message: {},
  isSending: false,
  isExporting: false,
  rawSchemaRequired: [],
};

export default Questionnaire;
